<template>
  <PageHeader eyebrow="Your profile" title="Account" description="View your run history."/>
  <div class="account-container">
    <div v-if="!isLoggedIn" class="account-login-prompt">
      <p>Sign in to save your run history.</p>
      <LoginButton />
    </div>

    <div v-else class="account-content">
      <div class="account-user-info">
        <img v-if="user.photoURL" :src="user.photoURL" :alt="user.displayName || user.email" class="account-avatar" referrerpolicy="no-referrer" />
        <span class="account-name">{{ user.displayName || user.email }}</span>
        <button class="black-button account-signout" @click="logout">Sign out</button>
      </div>

      <!-- Username section -->
      <div class="username-section">
        <div v-if="!editingUsername" class="username-display">
          <span class="username-label">Username</span>
          <span v-if="username" class="username-value">{{ username }}</span>
          <span v-else class="username-unset">Not set</span>
          <button class="black-button username-edit-btn" @click="startEditUsername">
            {{ username ? 'Change' : 'Set username' }}
          </button>
        </div>

        <div v-else class="username-editor">
          <span class="username-label">Username</span>
          <div class="username-input-row">
            <input
              v-model="usernameInput"
              class="username-input"
              type="text"
              placeholder="Choose a username"
              maxlength="30"
              @input="onUsernameInput"
              @keydown.enter="saveUsername"
              @keydown.escape="cancelEditUsername"
            />
            <button class="black-button" :disabled="!canSaveUsername" @click="saveUsername">Save</button>
            <button class="black-button username-cancel-btn" @click="cancelEditUsername">Cancel</button>
          </div>
          <div class="username-suggestions">
            <button class="suggestion-btn" @click="applySuggestion">Suggest username</button>
          </div>
          <div v-if="usernameStatus" :class="['username-status', usernameStatusClass]">{{ usernameStatus }}</div>
        </div>
      </div>

      <div v-if="loading" class="account-loading">Loading…</div>

      <div v-else-if="runs.length === 0" class="account-empty">
        <p>No runs yet. Complete a challenge set to see your history here.</p>
        <button class="black-button" @click="goHome">Start a run</button>
      </div>

      <template v-else>
        <div v-if="showUsernameRequired" class="publish-username-notice">
          A username is required to publish runs.
          <button class="suggestion-btn" @click="openUsernameEditor">Set username</button>
        </div>

        <div class="runs-table-wrap">
          <table class="runs-table">
            <thead>
              <tr>
                <th>Date</th>
                <th class="col-hide-mobile">Complexity</th>
                <th class="col-hide-mobile">Challenges</th>
                <th>Score</th>
                <th class="col-hide-mobile">Correct</th>
                <th class="col-hide-mobile">Sec / challenge</th>
                <th class="col-hide-mobile">Dare result</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="run in runs" :key="run.id">
                <td>{{ formatDate(run.completedAt) }}</td>
                <td class="col-hide-mobile">{{ run.complexity }}</td>
                <td class="col-hide-mobile">{{ run.challengeAmount }}</td>
                <td>{{ run.score }}</td>
                <td class="col-hide-mobile" :class="scoreClass(run.correctPct ?? run.score)">{{ run.correctPct != null ? run.correctPct + '%' : '—' }}</td>
                <td class="col-hide-mobile">{{ run.timePerChallenge }}s</td>
                <td class="col-hide-mobile dare-result-cell">
                  <template v-if="run.challengerScore != null">
                    <span :class="run.challengerScore < run.score ? 'score-good' : run.challengerScore > run.score ? 'score-low' : 'score-mid'">
                      {{ run.challengerScore }}
                    </span>
                    <span class="dare-result-pct">({{ run.challengerCorrectPct }}%)</span>
                  </template>
                  <span v-else-if="run.shared" class="dare-pending">pending</span>
                  <span v-else>—</span>
                </td>
                <td class="run-actions">
                  <button v-if="run.challenges" class="black-button replay-button" @click="review(run)">
                    <i class="ri-eye-line"></i>
                    <span class="col-hide-mobile">&nbsp;Review</span>
                  </button>
                  <button
                    v-if="run.challenges"
                    class="black-button dare-button"
                    :disabled="sharingRunId === run.id"
                    title="Dare a friend to beat this run"
                    @click="dare(run)"
                  >
                    <i class="ri-sword-line"></i>
                    <span class="col-hide-mobile">&nbsp;Dare</span>
                  </button>
                  <button
                    class="black-button publish-button"
                    :disabled="publishingRunId === run.id"
                    :title="publishedRunIds.has(run.id) ? 'Unpublish from leaderboard' : 'Publish to leaderboard'"
                    @click="togglePublish(run)"
                  >
                    <i :class="publishedRunIds.has(run.id) ? 'ri-folder-close-line' : 'ri-folder-open-line'"></i>
                    <span class="col-hide-mobile">&nbsp;{{ publishedRunIds.has(run.id) ? 'Unpublish' : 'Publish' }}</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <ShareSheet
        v-if="shareSheetRunId"
        :url="`${origin}/#/dare/${shareSheetRunId}`"
        @close="shareSheetRunId = null"
      />
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import {useRouter} from 'vue-router';
import {useAuth} from '@/composables/useAuth';
import LoginButton from '@/components/parts/LoginButton.vue';
import PageHeader from '@/components/parts/PageHeader.vue';
import ShareSheet from '@/components/parts/ShareSheet.vue';
import {generateUsername} from 'unique-username-generator';

const router = useRouter();
const {
  user, isLoggedIn, logout, fetchRuns, completeMagicLinkSignIn,
  username, isUsernameAvailable, setUsername,
  publishRun, unpublishRun, fetchPublishedRunIds,
  shareDareRun,
} = useAuth();

const runs = ref([]);
const loading = ref(false);
const publishedRunIds = ref(new Set());
const publishingRunId = ref(null);
const showUsernameRequired = ref(false);
const shareSheetRunId = ref(null);
const sharingRunId = ref(null);
const origin = window.location.origin;

// Username editing state
const editingUsername = ref(false);
const usernameInput = ref('');
const usernameStatus = ref('');
const usernameAvailable = ref(null);
let checkTimer = null;

const canSaveUsername = computed(() => {
  return usernameInput.value.trim().length >= 2 && usernameAvailable.value === true;
});

const usernameStatusClass = computed(() => {
  if (usernameAvailable.value === true) return 'status-ok';
  if (usernameAvailable.value === false) return 'status-err';
  return 'status-checking';
});

function startEditUsername() {
  usernameInput.value = username.value ?? '';
  usernameStatus.value = '';
  usernameAvailable.value = null;
  editingUsername.value = true;
}

function cancelEditUsername() {
  editingUsername.value = false;
  usernameStatus.value = '';
}

function onUsernameInput() {
  const val = usernameInput.value.trim();
  usernameStatus.value = '';
  usernameAvailable.value = null;
  clearTimeout(checkTimer);
  if (val.length < 2) return;
  if (!/^[a-zA-Z0-9_-]+$/.test(val)) {
    usernameStatus.value = 'Only letters, numbers, _ and - allowed';
    usernameAvailable.value = false;
    return;
  }
  usernameStatus.value = 'Checking…';
  checkTimer = setTimeout(async () => {
    const available = await isUsernameAvailable(val);
    if (available || val.toLowerCase() === username.value?.toLowerCase()) {
      usernameAvailable.value = true;
      usernameStatus.value = 'Available';
    } else {
      usernameAvailable.value = false;
      usernameStatus.value = 'Already taken';
    }
  }, 400);
}

async function saveUsername() {
  if (!canSaveUsername.value) return;
  try {
    await setUsername(usernameInput.value.trim());
    editingUsername.value = false;
  } catch (e) {
    usernameStatus.value = e.message ?? 'Failed to save username';
    usernameAvailable.value = false;
  }
}

function applySuggestion() {
  usernameInput.value = generateUsername('', 2, 20);
  onUsernameInput();
}

async function dare(run) {
  if (sharingRunId.value) return;
  sharingRunId.value = run.id;
  try {
    if (!run.shared) {
      await shareDareRun(run.id);
      run.shared = true;
    }
    shareSheetRunId.value = run.id;
  } catch (err) {
    console.error('Failed to share run:', err);
  } finally {
    sharingRunId.value = null;
  }
}

async function togglePublish(run) {
  if (!username.value) {
    showUsernameRequired.value = true;
    return;
  }
  publishingRunId.value = run.id;
  try {
    if (publishedRunIds.value.has(run.id)) {
      await unpublishRun(run.id);
      publishedRunIds.value = new Set([...publishedRunIds.value].filter(id => id !== run.id));
    } else {
      await publishRun(run);
      publishedRunIds.value = new Set([...publishedRunIds.value, run.id]);
    }
  } catch (err) {
    console.error('Publish toggle failed:', err);
  } finally {
    publishingRunId.value = null;
  }
}

function openUsernameEditor() {
  showUsernameRequired.value = false;
  startEditUsername();
  document.querySelector('.username-section')?.scrollIntoView({behavior: 'smooth'});
}

watch(username, val => {
  if (val) showUsernameRequired.value = false;
});

async function loadRuns() {
  if (!isLoggedIn.value) return;
  loading.value = true;
  try {
    [runs.value, publishedRunIds.value] = await Promise.all([
      fetchRuns(),
      fetchPublishedRunIds(),
    ]);
  } catch (err) {
    console.error('Failed to fetch runs:', err);
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await completeMagicLinkSignIn();
  loadRuns();
});
watch(isLoggedIn, (loggedIn) => {
  if (loggedIn) loadRuns();
  else runs.value = [];
});

function goHome() {
  router.push({name: 'home'});
}

function review(run) {
  router.push({
    name: 'test',
    query: {
      replayId: run.id,
      review: 'true',
      challengeAmount: run.challengeAmount,
    },
  });
}

function formatDate(ts) {
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function scoreClass(score) {
  if (score >= 80) return 'score-good';
  if (score >= 50) return 'score-mid';
  return 'score-low';
}
</script>

<style scoped>
.account-container {
  width: 100%;
}

.account-login-prompt,
.account-empty,
.account-loading {
  text-align: left;
  color: var(--text-color);
  padding: 20px 0;
}

.account-user-info {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
}

.account-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.account-name {
  font-size: 14px;
  color: var(--text-color-bright);
  flex: 1;
}

.account-signout {
  min-width: unset;
}

/* Username section */
.username-section {
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--border-color);
}

.username-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-color);
  display: block;
  margin-bottom: 8px;
}

.username-display {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.username-value {
  font-size: 15px;
  color: var(--text-color-bright);
  flex: 1;
}

.username-unset {
  font-size: 14px;
  color: var(--text-color);
  flex: 1;
}

.username-edit-btn {
  min-width: unset;
}

.username-editor {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.username-input-row {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.username-input {
  flex: 1;
  min-width: 160px;
  background: var(--hover-bg);
  border: 1px solid var(--border-color-bright);
  color: var(--text-color-bright);
  font-family: var(--font), monospace;
  font-size: 15px;
  padding: 7px 10px;
  border-radius: 4px;
  outline: none;
}

.username-input:focus {
  border-color: var(--hover-border);
}

.username-cancel-btn {
  min-width: unset;
  background: transparent;
  border-color: var(--border-color-bright);
  color: var(--text-color);
}

.username-suggestions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.suggestion-btn {
  background: none;
  border: 1px solid var(--border-color-bright);
  color: var(--text-color);
  font-family: var(--font), monospace;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.suggestion-btn:hover {
  border-color: var(--hover-border);
  color: var(--text-color-bright);
}

.username-status {
  font-size: 12px;
}

.status-ok { color: #4caf50; }
.status-err { color: var(--geogaddi-r); }
.status-checking { color: var(--text-color); }

/* Publish notice */
.publish-username-notice {
  font-size: 13px;
  color: var(--geogaddi-y);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* Runs table */
.runs-table-wrap {
  overflow-x: auto;
}

.runs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  color: var(--text-color-bright);
}

.runs-table th,
.runs-table td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid var(--highlighted);
}

.runs-table th {
  color: var(--text-color);
  font-weight: normal;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.05em;
}

.score-good { color: #4caf50; }
.score-mid  { color: var(--geogaddi-y); }
.score-low  { color: var(--geogaddi-r); }

@media (max-width: 639px) {
  .runs-table th,
  .runs-table td {
    padding: 8px 6px;
  }
}

.run-actions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  align-items: center;
}

.replay-button,
.publish-button,
.dare-button {
  min-width: unset;
}

.dare-result-cell {
  font-size: 14px;
  color: var(--text-color-bright);
}

.dare-result-pct {
  margin-left: 4px;
  font-size: 12px;
  color: var(--text-color);
}

.dare-pending {
  font-size: 12px;
  color: var(--text-color);
  font-style: italic;
}

@media screen and (min-width: 640px) {
  .publish-button {
    min-width: 140px;
  }
}
</style>
